import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {MatIconModule} from '@angular/material/icon';
import {Store, select} from '@ngrx/store';
import {Observable, Subject, filter, map, takeUntil} from 'rxjs';
import {animate, style, transition, trigger} from '@angular/animations';

import {MENU_ITEMS, MENU_ITEMS_MAP, MENU_PATHS, MenuItem} from 'src/app/app.constants';
import {selectActiveUserRole} from 'src/app/core/store/active-user/active-user.selector';
import {getMenuItemsByRole} from 'src/app/shared/directives/utils/get-role-permission-by-key';
import {MobileService} from 'src/app/core/page/mobile.service';
import {MenuRoutesService} from 'src/app/shared/layout/services/menu-routes.service';
import {
  UpdateCurrentProjectsRouteAction,
  UpdateScrollPositionAction,
} from 'src/app/core/store/projects/projects.action';
import {Role} from 'src/app/core/model/role';

const PROJECT_MENU_ITEMS: MenuItem[] = [
  MENU_ITEMS_MAP.Projects,
  MENU_ITEMS_MAP.Schedule,
  MENU_ITEMS_MAP.Rewards,
  MENU_ITEMS_MAP.Tokens,
  MENU_ITEMS_MAP.Wiki,
  MENU_ITEMS_MAP.Trackers,
  MENU_ITEMS_MAP.Reporting,
];

@Component({
  selector: 'projects-routing',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './projects-routing.component.html',
  styleUrl: './projects-routing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('swipe', [
      transition(':increment', [
        style({transform: 'translateX(100%)'}),
        animate('350ms ease-out', style({transform: 'translateX(0)'})),
      ]),
      transition(':decrement', [
        style({transform: 'translateX(-100%)'}),
        animate('350ms ease-out', style({transform: 'translateX(0)'})),
      ]),
    ]),
  ],
})
export class ProjectsRoutingComponent implements OnDestroy {
  @HostBinding('class') hostClasses = 'oph-feature-layout';
  @HostBinding('class.swipe-animating') isAnimating = false;

  private unsubscribe$ = new Subject<void>();
  private currentRoutes: MenuItem[] = [];
  private resizeObserver: ResizeObserver | null = null;

  private navScrollRef = viewChild<ElementRef<HTMLElement>>('navScroll');
  showEndFade = signal(false);

  activeRouteIndex = signal(0);

  userRole$: Observable<Role> = this.store$.pipe(select(selectActiveUserRole));
  filteredMenuItems$: Observable<MenuItem[]>;
  visibleRoutes$: Observable<MenuItem[]>;

  constructor(
    private store$: Store,
    private router: Router,
    private menuRoutesService: MenuRoutesService,
    private mobileService: MobileService,
    private cdr: ChangeDetectorRef
  ) {
    this.filteredMenuItems$ = this.observeUserRole();
    this.visibleRoutes$ = this.menuRoutesService.getFilteredRoutes$(PROJECT_MENU_ITEMS, this.filteredMenuItems$);

    this.visibleRoutes$.pipe(takeUntil(this.unsubscribe$)).subscribe(routes => {
      this.currentRoutes = routes;
      const idx = routes.findIndex(r => this.router.url?.startsWith(r.url));
      if (idx >= 0) {
        this.activeRouteIndex.set(idx);
      }
    });

    afterNextRender(() => {
      this.checkScrollOverflow();
      const navEl = this.navScrollRef()?.nativeElement;
      if (navEl) {
        this.resizeObserver = new ResizeObserver(() => this.checkScrollOverflow());
        this.resizeObserver.observe(navEl);
      }
    });

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        const newIdx = this.currentRoutes.findIndex(r => url?.startsWith(r.url));
        if (newIdx >= 0 && newIdx !== this.activeRouteIndex()) {
          this.activeRouteIndex.set(newIdx);
        }
      });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onNavScroll(): void {
    this.checkScrollOverflow();
  }

  private checkScrollOverflow(): void {
    const el = this.navScrollRef()?.nativeElement;
    if (!el) return;
    this.showEndFade.set(el.scrollWidth > el.clientWidth + el.scrollLeft + 1);
  }

  onRouteClick(item: MenuItem): void {
    const currentUrl = this.router.url?.split('?')[0];

    if (item.url === MENU_PATHS.Projects && currentUrl?.startsWith(MENU_PATHS.Projects)) {
      this.store$.dispatch(new UpdateCurrentProjectsRouteAction({route: MENU_PATHS.Projects}));
    } else if (item.url !== MENU_PATHS.Projects && currentUrl?.startsWith(MENU_PATHS.Projects)) {
      const routeToStore = currentUrl.replace(/\/task\/[^/]+$/, '');
      this.store$.dispatch(new UpdateCurrentProjectsRouteAction({route: routeToStore}));
      this.store$.dispatch(new UpdateScrollPositionAction({scrollPosition: 0}));
    }

    this.router.navigate([item.url]);
  }

  isActive(item: MenuItem): boolean {
    return this.router.url?.startsWith(item.url);
  }

  onSwipeStart(): void {
    this.isAnimating = true;
    this.cdr.markForCheck();
  }

  onSwipeDone(): void {
    this.isAnimating = false;
    this.cdr.markForCheck();
  }

  observeUserRole(): Observable<MenuItem[]> {
    return this.userRole$.pipe(
      filter(role => !!role),
      map((role: Role) => {
        const roleDisplayName = role.displayName?.toLowerCase();
        const rolePermissions = role.permissions;
        const isNative = this.mobileService.isMobilePlatform();

        let menuItems = MENU_ITEMS;

        if (roleDisplayName !== 'admin') {
          menuItems = menuItems.filter(item => !item.adminOnly);
          menuItems = getMenuItemsByRole(rolePermissions, menuItems);
        }

        if (!isNative) {
          menuItems = menuItems.filter(item => !item.mobileAppOnly);
        }

        return menuItems;
      }),
      takeUntil(this.unsubscribe$)
    );
  }
}
